/**
 * Rechnung schreiben.
 *
 * Angelegt wird immer ein Entwurf. Die Nummer entsteht erst beim Stellen —
 * ein verworfener Entwurf darf keine Lücke in der Nummernfolge
 * hinterlassen, und lückenlose Rechnungsnummern sind Pflicht.
 *
 * Fehlen dem Unternehmen Pflichtangaben, steht das hier und nicht erst beim
 * Stellen: Wer eine Rechnung fertig getippt hat und dann erfährt, dass die
 * Steuernummer fehlt, hat umsonst getippt.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Textbereich, Textfeld } from '../bausteine/Feld';
import { KontaktWahl } from '../bausteine/KontaktWahl';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import {
  alsEingabe,
  alsZeilen,
  leerePosition,
  Positionen,
  type PositionZeile,
} from '../bausteine/Positionen';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { heute } from '../lib/format';
import type { Kontakt, KontaktListe, Rechnung, Unternehmen } from '../lib/typen';

/**
 * Dieselbe Maske legt an und ändert.
 *
 * Ein Entwurf, der sich nicht korrigieren lässt, wäre eine Falle: Ein
 * Tippfehler in einer Position hieße verwerfen und alles neu tippen. Geändert
 * wird ausschließlich der Entwurf — sobald die Rechnung gestellt ist, weist
 * die API jede Änderung ab, denn dann liegt sie beim Empfänger.
 */
export function RechnungNeu(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const bestand = useAbruf<Rechnung>(id ? `/api/rechnungen/${id}` : null);

  if (id && !bestand.daten) {
    return (
      <Seite titel="Rechnung ändern">
        {bestand.fehler ? <Fehlermeldung text={bestand.fehler} /> : <Laedt />}
      </Seite>
    );
  }

  // Der Schlüssel erzwingt einen frischen Formularzustand, sobald ein
  // anderer Entwurf geladen ist — useState übernimmt Anfangswerte nur beim
  // ersten Aufbau.
  return <Formular key={bestand.daten?.id ?? 'neu'} bestand={bestand.daten} />;
}

function Formular({ bestand }: { bestand: Rechnung | null }): ReactNode {
  const navigiere = useNavigate();
  const vorgang = useVorgang();

  const kontakte = useAbruf<KontaktListe>('/api/kontakte?typ=kunde&proSeite=200');
  const unternehmen = useAbruf<Unternehmen>('/api/unternehmen');

  const [kontaktId, setzeKontaktId] = useState(bestand?.kontaktId ?? '');
  const [empfaengerName, setzeEmpfaengerName] = useState(bestand?.empfaengerName ?? '');
  const [rechnungsdatum, setzeRechnungsdatum] = useState(bestand?.rechnungsdatum ?? heute());
  const [betreff, setzeBetreff] = useState(bestand?.betreff ?? '');
  const [leistungVon, setzeLeistungVon] = useState(bestand?.leistungVon ?? '');
  const [leistungBis, setzeLeistungBis] = useState(bestand?.leistungBis ?? '');
  const [zahlungsziel, setzeZahlungsziel] = useState('');
  const [einleitung, setzeEinleitung] = useState(bestand?.einleitungstext ?? '');
  const [schluss, setzeSchluss] = useState(bestand?.schlusstext ?? '');
  const [zeilen, setzeZeilen] = useState<PositionZeile[]>(
    bestand && bestand.positionen.length > 0 ? alsZeilen(bestand.positionen) : [leerePosition()],
  );

  const kleinunternehmer = unternehmen.daten?.kleinunternehmer ?? true;
  const kunden = kontakte.daten?.kontakte ?? [];

  const fertig =
    kontaktId !== '' && zeilen.some((z) => z.bezeichnung.trim() !== '' && z.einzelpreis !== '');

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!fertig) return;

    await vorgang.fuehreAus(async () => {
      const daten = {
        kontaktId,
        empfaengerName: empfaengerName.trim() || null,
        rechnungsdatum,
        betreff: betreff.trim() || null,
        leistungVon: leistungVon || null,
        leistungBis: leistungBis || null,
        ...(zahlungsziel ? { zahlungszielTage: Number.parseInt(zahlungsziel, 10) } : {}),
        ...(einleitung.trim() ? { einleitungstext: einleitung.trim() } : {}),
        ...(schluss.trim() ? { schlusstext: schluss.trim() } : {}),
        positionen: alsEingabe(zeilen),
      };

      const gespeichert = bestand
        ? await api.aendern<Rechnung>(`/api/rechnungen/${bestand.id}`, daten)
        : await api.senden<Rechnung>('/api/rechnungen', daten);

      navigiere(`/rechnungen/${bestand?.id ?? gespeichert.id}`);
      return gespeichert;
    });
  }

  return (
    <Seite
      titel={bestand ? 'Rechnung ändern' : 'Rechnung schreiben'}
      untertitel="Entwurf"
    >
      <form className="stapel" style={{ maxWidth: '1100px' }} onSubmit={(e) => void speichern(e)}>
        <Fehlermeldung text={vorgang.fehler} />

        {unternehmen.daten && !unternehmen.daten.rechnungsfaehig ? (
          <Meldung ton="warnung" titel="Stammdaten unvollständig.">
            Es fehlt: {unternehmen.daten.fehlendeAngaben.join(', ')}. Der
            Entwurf lässt sich speichern, das Stellen wird ohne diese
            Angaben abgewiesen — sie gehören auf jede Rechnung.{' '}
            <Link to="/stammdaten">Jetzt ergänzen</Link>.
          </Meldung>
        ) : null}

        {kontakte.daten && kunden.length === 0 ? (
          <Meldung ton="info" titel="Noch kein Kunde erfasst.">
            Eine Rechnung braucht einen Empfänger mit Anschrift.{' '}
            <Link to="/kontakte">Kunden anlegen</Link>.
          </Meldung>
        ) : null}

        <div className="karte">
          <div className="karte-kopf">
            <h2>Empfänger und Fristen</h2>
          </div>
          <div className="karte-koerper formularraster">
            <KontaktWahl
              beschriftung="Empfänger"
              wert={kontaktId}
              setzeWert={(id) => {
                setzeKontaktId(id);
                // Ein gewechselter Empfänger macht einen Namen ungültig,
                // der zum vorigen gehörte. Stehen lassen wäre die
                // gefährlichere Variante: Die Rechnung ginge an den einen
                // Kunden und trüge den Namen des anderen.
                setzeEmpfaengerName('');
              }}
              kontakte={kunden}
              typ="kunde"
              mitAnschrift
              pflicht
              hinweis="Anschrift und Zahlungsziel kommen aus dem Kontakt."
              neuGeladen={kontakte.neuLaden}
            />

            <EmpfaengerName
              kontakt={kunden.find((k) => k.id === kontaktId) ?? null}
              wert={empfaengerName}
              setzeWert={setzeEmpfaengerName}
            />

            <Textfeld
              beschriftung="Rechnungsdatum"
              art="date"
              wert={rechnungsdatum}
              setzeWert={setzeRechnungsdatum}
              pflicht
            />

            <Textfeld
              beschriftung="Zahlungsziel in Tagen"
              art="number"
              wert={zahlungsziel}
              setzeWert={setzeZahlungsziel}
              klasse="zahl"
              hinweis={
                unternehmen.daten
                  ? `leer = ${unternehmen.daten.rechnungZahlungszielTage} Tage aus den Stammdaten`
                  : 'leer = Vorgabe aus den Stammdaten'
              }
            />

            <Textfeld
              beschriftung="Betreff"
              wert={betreff}
              setzeWert={setzeBetreff}
              breit
              maxLaenge={200}
              platzhalter="App-Entwicklung Juli 2026"
            />

            <Textfeld
              beschriftung="Leistung von"
              art="date"
              wert={leistungVon}
              setzeWert={setzeLeistungVon}
              hinweis="Leistungszeitraum, falls zutreffend"
            />

            <Textfeld
              beschriftung="Leistung bis"
              art="date"
              wert={leistungBis}
              setzeWert={setzeLeistungBis}
            />
          </div>
        </div>

        <Positionen zeilen={zeilen} setzeZeilen={setzeZeilen} mitSteuer={!kleinunternehmer} />

        {kleinunternehmer ? (
          <Meldung ton="info">
            Als Kleinunternehmer nach § 19 UStG wird keine Umsatzsteuer
            ausgewiesen. Der entsprechende Hinweis steht automatisch auf der
            Rechnung.
          </Meldung>
        ) : null}

        <div className="karte">
          <div className="karte-kopf">
            <h2>Texte</h2>
            <span className="klein still">leer = Vorgabe aus den Stammdaten</span>
          </div>
          <div className="karte-koerper formularraster">
            <Textbereich
              beschriftung="Einleitung"
              wert={einleitung}
              setzeWert={setzeEinleitung}
              zeilen={2}
              breit
              platzhalter={unternehmen.daten?.rechnungEinleitungstext ?? ''}
            />
            <Textbereich
              beschriftung="Schlusstext"
              wert={schluss}
              setzeWert={setzeSchluss}
              zeilen={2}
              breit
              platzhalter={unternehmen.daten?.rechnungSchlusstext ?? ''}
            />
          </div>
        </div>

        <div className="reihe">
          <button type="submit" className="knopf haupt" disabled={!fertig || vorgang.laeuft}>
            {vorgang.laeuft ? 'Wird gespeichert …' : 'Entwurf speichern'}
          </button>
          <button
            type="button"
            className="knopf"
            onClick={() => navigiere(bestand ? `/rechnungen/${bestand.id}` : '/rechnungen')}
            disabled={vorgang.laeuft}
          >
            Abbrechen
          </button>
          <span className="klein still">
            Gestellt wird im nächsten Schritt — dann entsteht die Nummer.
          </span>
        </div>
      </form>
    </Seite>
  );
}

/**
 * Welcher Name in der Anschrift steht.
 *
 * Der Kontakt trägt zwei brauchbare Namen: den der Firma und den der
 * Person dahinter. Welcher auf die Rechnung gehört, hängt vom Kunden ab
 * und manchmal von der einzelnen Rechnung — ein zweiter Kontakt für
 * dieselbe Firma wäre die falsche Antwort, denn dann verteilen sich
 * offene Posten und IBAN auf zwei Datensätze, und die Zuordnung der
 * Bankumsätze findet nur noch die Hälfte.
 *
 * Deshalb hier: zwei Knöpfe für die naheliegenden Fälle und ein Feld für
 * alles andere. Leer heißt "Name des Kontakts" — dann friert das Stellen
 * ihn ein, wie bisher.
 */
function EmpfaengerName({
  kontakt,
  wert,
  setzeWert,
}: {
  kontakt: Kontakt | null;
  wert: string;
  setzeWert: (wert: string) => void;
}): ReactNode {
  const vorschlaege = [kontakt?.name, kontakt?.inhaber].filter(
    (n): n is string => typeof n === 'string' && n.trim() !== '',
  );

  return (
    <div className="feld breit">
      <label htmlFor="empfaengername">Empfängername auf der Rechnung</label>
      <input
        id="empfaengername"
        type="text"
        maxLength={200}
        value={wert}
        placeholder={kontakt?.name ?? 'Name des Kontakts'}
        onChange={(e) => setzeWert(e.target.value)}
      />
      {vorschlaege.length > 0 ? (
        <div className="reihe klein" style={{ marginTop: '0.35rem' }}>
          {vorschlaege.map((name) => (
            <button
              key={name}
              type="button"
              className="knopf schlicht klein"
              onClick={() => setzeWert(name)}
            >
              {name}
            </button>
          ))}
          {wert !== '' ? (
            <button type="button" className="knopf schlicht klein" onClick={() => setzeWert('')}>
              zurücksetzen
            </button>
          ) : null}
        </div>
      ) : null}
      <span className="hinweis">
        {kontakt?.inhaber
          ? 'Leer = Name des Kontakts. Der Inhaber steht als zweiter Vorschlag bereit.'
          : 'Leer = Name des Kontakts. Ein Inhaber lässt sich am Kontakt hinterlegen.'}
      </span>
    </div>
  );
}
